import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  Blockquote,
  CTAButton,
  H2,
  HeroH1,
  P,
  SeoPageShell,
  SpeakerLine,
} from "./seo/SeoPageLayout";

const WhyExhaustedAfterConversations = () => {
  return (
    <SeoPageShell>
      <SEO
        title="Why You Feel Exhausted After Certain Conversations"
        description="If you replay a conversation and still can't explain why you're drained, there's a pattern underneath it. See it named — free, private, seconds."
        path="/why-do-i-feel-exhausted-after-conversations"
      />

      <HeroH1>Why Do I Feel Exhausted After Conversations With This Person?</HeroH1>

      <P>
        The conversation felt normal. No one raised their voice. No one insulted you. And yet you
        walked away feeling like you'd lost something you couldn't name.
      </P>
      <P>
        You replay it on the drive home. You draft three different texts and send none of them.
        You wonder, again, why a five-minute exchange has eaten the rest of your afternoon.
      </P>
      <P>
        The exhaustion is data. Your body is registering a pattern before your mind has language
        for it.
      </P>

      <H2>The exhaustion comes before the explanation</H2>
      <P>
        Almost everyone in this kind of dynamic feels the cost long before they can articulate the
        cause. The fog. The second-guessing. The rehearsing of what you should have said. The
        strange sense that you were the one being unreasonable, even though, if you write it out
        afterwards, nothing you said was unreasonable.
      </P>
      <P>
        This is not a character flaw. It is one of the most common experiences in dynamics like
        these, and it has a structure.
      </P>

      <H2>What your nervous system noticed</H2>
      <P>
        Forget interpretations for a moment. Just observe the mechanics of the conversation:
        the goalposts that quietly move, the question you asked that never actually gets answered,
        the way concern arrives wrapped around control, the apology that somehow ends with you
        apologizing for needing the apology.
      </P>
      <P>A short example of how this lands in text:</P>
      <Blockquote>
        <SpeakerLine speaker="You" line="It hurt when you said that in front of my friends." />
        <SpeakerLine
          speaker="Them"
          line="I can't believe you'd embarrass me by bringing this up now. After everything I've done for you this week."
        />
      </Blockquote>
      <P>
        You came in with one thing. You're now defending yourself on three different fronts, none
        of them the thing you actually said.
      </P>

      <H2>Why you couldn't put it into words</H2>
      <P>
        These patterns are designed to be deniable. Each individual line is defensible in
        isolation — "I was just upset," "I didn't mean it like that," "you're reading into it."
        The harm lives in the pattern across the whole exchange, which is exactly what's hard to
        hold in your head while it's happening to you.
      </P>
      <P>
        This is why people so often describe finally "understanding" or "having words for it" as a
        turning point. Naming the mechanism is not a small thing. It is the thing.
      </P>

      <CTAButton />

      <H2>Naming it is the first step to thinking clearly again</H2>
      <P>
        Articulation restores agency. When you can name what's being done — when you can say
        "that was a blame-flip" or "that was an invalidation" rather than "I don't know, I just
        feel weird" — the pattern stops running the room. It becomes something you are watching,
        instead of something happening inside you.
      </P>
      <P>
        Some people now run their actual conversations through analysis just to see the pattern
        laid out plainly. Not to be told what to do — to be shown what was already there. It's
        part of{" "}
        <Link
          to="/articulation-before-adjudication"
          className="text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
        >
          the emerging category these tools belong to →
        </Link>
      </P>

      <H2>Related patterns</H2>
      <ul className="mt-4 space-y-3 list-none pl-0">
        <li>
          <Link
            to="/darvo-examples"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            When they flip the blame: DARVO examples →
          </Link>
        </li>
        <li>
          <Link
            to="/reactive-abuse-examples"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            When you become the villain: reactive abuse examples →
          </Link>
        </li>
        <li>
          <Link
            to="/why-do-i-look-like-the-crazy-one"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            When you become the one who looks unstable →
          </Link>
        </li>
        <li>
          <Link
            to="/emotional-invalidation-examples"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            When your feelings get erased: emotional invalidation examples →
          </Link>
        </li>
        <li>
          <Link
            to="/why-do-they-disappear-and-come-back"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            When they disappear and come back →
          </Link>
        </li>
        <li>
          <Link
            to="/why-do-i-feel-like-im-walking-on-eggshells"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Why you feel like you're walking on eggshells →
          </Link>
        </li>
        <li>
          <Link
            to="/manipulative-text-message-analysis"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Run an actual conversation through analysis →
          </Link>
        </li>
        <li>
          <Link
            to="/ai-detect-narcissistic-abuse"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Researchers built an AI that detects these patterns in text →
          </Link>
        </li>
      </ul>

      <CTAButton />
    </SeoPageShell>
  );
};

export default WhyExhaustedAfterConversations;
